import React from "react";
import BreadCrumbs from "../../components/breadCrumb";
import Button from "../../components/button";
import { cn } from "../../general";
import AccountDetailsSection from "./AccountDetailsSection";
import AccessSettingsSection from "./AccessSettingsSection";
import type { BadgeProps } from "../../components/badge";
import type { UserDetailsViewProps } from "./type";

type BadgeVariant = BadgeProps["variant"];

const toTitleCase = (value?: string) => {
  if (!value) return "";
  return value
    .toLowerCase()
    .split(" ")
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
};

const getStatusBadgeVariant = (status?: string): BadgeVariant => {
  if (!status) {
    return "default";
  }

  const normalized = status.toLowerCase();

  if (normalized === "active" || normalized === "accepted") {
    return "active";
  }

  if (normalized === "pending") {
    return "pending";
  }

  if (normalized === "suspended" || normalized === "inactive") {
    return "offline";
  }

  return "default";
};

const UserDetailsView: React.FC<UserDetailsViewProps> = (props) => {
  const {
    breadcrumbItems = [],
    headerTitle,
    headerSubtitle,
    onDelete,
    deleteLabel,
    showDelete = Boolean(onDelete),
    accountDetails,
    accessSettings,
    statusBadgeVariant,
    statusLabel,
    showActions = false,
    actionPrimaryLabel,
    actionSecondaryLabel,
    onPrimaryAction,
    onSecondaryAction,
    disablePrimaryAction = false,
    disableSecondaryAction = false,
    className,
    labels,
  } = props;

  const labelOverrides = labels ?? {};

  const accountDetailsTitle =
    labelOverrides.accountDetailsTitle ?? "Account details";
  const accessSettingsTitle =
    labelOverrides.accessSettingsTitle ?? "Access settings";

  const firstNameLabel = labelOverrides.firstName ?? "First name";
  const lastNameLabel = labelOverrides.lastName ?? "Last name";
  const emailLabel = labelOverrides.email ?? "Email address";
  const phoneLabel = labelOverrides.phoneNumber ?? "Phone number";
  const statusLabelText = labelOverrides.status ?? "Status";
  const dateCreatedLabel = labelOverrides.dateCreated ?? "Date created";
  const lastLoginLabel = labelOverrides.lastLogin ?? "Last login";
  const roleLabel = labelOverrides.role ?? "Role";
  const branchLabel = labelOverrides.branch ?? "Branch";

  const deleteButtonLabel =
    labelOverrides.deleteLabel ?? deleteLabel ?? "Delete";
  const primaryActionLabel =
    labelOverrides.primaryAction ?? actionPrimaryLabel ?? "Save changes";
  const secondaryActionLabel =
    labelOverrides.secondaryAction ?? actionSecondaryLabel ?? "Cancel";

  const {
    firstName = "",
    lastName = "",
    email = "",
    phoneNumber = "",
    status,
    dateCreated,
    lastLogin,
    userId,
  } = accountDetails ?? {};

  const subtitleToRender = headerSubtitle ?? userId ?? "";
  const derivedStatusVariant =
    statusBadgeVariant ?? getStatusBadgeVariant(status);
  const derivedStatusLabel = statusLabel ?? (status ? toTitleCase(status) : "");
  const shouldRenderAccessSection = accessSettings !== undefined || showActions;

  return (
    <div className={cn("min-h-screen", className)}>
      <div className="border-b border-[#E9EAEB] bg-white py-4">
        <div className="mx-auto flex w-full max-w-none px-4 sm:px-6 lg:max-w-7xl xl:max-w-none xl:px-8 flex-col gap-4">
          {breadcrumbItems.length > 0 && (
            <BreadCrumbs items={breadcrumbItems} />
          )}

          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-xl sm:text-2xl font-semibold text-[#181D27]">
                {headerTitle}
              </h1>
              {subtitleToRender && (
                <p className="text-sm text-[#535862]">{subtitleToRender}</p>
              )}
            </div>

            {(showDelete || onDelete) && (
              <Button
                variant="outline"
                size="sm"
                className="self-start border-transparent bg-transparent text-[#D92D20] hover:bg-[#FEE4E2]"
                onClick={onDelete}
              >
                {deleteButtonLabel}
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="mx-auto w-full max-w-none px-4 sm:px-6 lg:max-w-7xl xl:max-w-none xl:px-8 py-4 sm:py-8">
        <div className="space-y-6">
          <AccountDetailsSection
            title={accountDetailsTitle}
            firstName={firstName}
            lastName={lastName}
            email={email}
            phoneNumber={phoneNumber}
            status={status}
            dateCreated={dateCreated}
            lastLogin={lastLogin}
            statusBadgeVariant={derivedStatusVariant}
            statusLabel={derivedStatusLabel}
            labels={{
              firstName: firstNameLabel,
              lastName: lastNameLabel,
              email: emailLabel,
              phoneNumber: phoneLabel,
              status: statusLabelText,
              dateCreated: dateCreatedLabel,
              lastLogin: lastLoginLabel,
            }}
          />

          {shouldRenderAccessSection && (
            <AccessSettingsSection
              title={accessSettingsTitle}
              role={accessSettings?.role}
              branch={accessSettings?.branch}
              showActions={showActions}
              actionPrimaryLabel={primaryActionLabel}
              actionSecondaryLabel={secondaryActionLabel}
              onPrimaryAction={onPrimaryAction}
              onSecondaryAction={onSecondaryAction}
              disablePrimaryAction={disablePrimaryAction}
              disableSecondaryAction={disableSecondaryAction}
              labels={{
                role: roleLabel,
                branch: branchLabel,
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default UserDetailsView;
export { default as UserDetailsViewSkeleton } from "./skeleton";
